import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('xru-language');
    return saved || 'en';
  });
  
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => {
    return localStorage.getItem('xru-language-selected') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('xru-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const selectLanguage = (lang) => {
    setLanguage(lang);
    setHasSelectedLanguage(true);
    localStorage.setItem('xru-language-selected', 'true');
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return fallback || key;
      }
    }
    
    return value || fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, selectLanguage, toggleLanguage, hasSelectedLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      ourWork: 'Our Work',
      news: 'News',
      legalHelp: 'Legal Help',
      partners: 'Partners',
      contact: 'Contact',
      alertMap: 'Alert Map',
      reportViolation: 'Report Violation',
      joinXRU: 'Join XRU'
    },
    
    // Common
    common: {
      learnMore: 'Learn More',
      submit: 'Submit',
      sending: 'Sending...',
      close: 'Close',
      viewAll: 'View All',
      readMore: 'Read More',
      email: 'Email',
      phone: 'Phone',
      name: 'Name',
      message: 'Message',
      location: 'Location',
      date: 'Date',
      time: 'Time',
      description: 'Description',
      subscribe: 'Subscribe',
      joinMovement: 'Join the Movement',
      reportNow: 'Report Now',
      callNow: 'Call Now',
      visitWebsite: 'Visit Website',
      quickLinks: 'Quick Links',
      contactUs: 'Contact Us',
      followUs: 'Follow Us',
      allRightsReserved: 'All rights reserved',
      defendingRights: 'Defending Human Rights. Demanding Justice. Building Power.',
      humanRights: 'Human Rights',
      civilRights: 'Civil Rights'
    },

    // Home Page
    home: {
      heroTitle: 'Defending Human Rights for Xicano & Indigenous Peoples',
      heroSubtitle: 'The Xicano Rights Union fights for the human rights, civil rights, and dignity of Xicano and Indigenous communities through advocacy, legal action, cultural education, and youth empowerment.',
      reportViolation: 'Report a Violation',
      joinMovement: 'Join the Movement',
      missionQuote: '"Every person deserves human rights. We advocate for policies that dismantle systemic injustices, preserve our cultural heritage, and empower the next generation of Xicano and Indigenous leaders."',
      topStories: 'Top Stories',
      viewAllNews: 'View All News',
      whatWeDo: 'What We Do',
      whatWeDoSubtitle: 'XRU is dedicated to defending human rights, fighting for civil rights, preserving cultural heritage, and empowering our youth through education and community organizing.',
      alertMapBanner: {
        title: 'Human Rights Alert Map',
        subtitle: 'Report and track enforcement activity in the Twin Cities. Protect human rights. Keep our community safe.',
        viewMap: 'View Alert Map',
        liveUpdates: 'Live Updates'
      },
      joinFight: 'Join the Fight for Justice',
      joinFightSubtitle: 'Your voice matters. Your membership supports our advocacy, cultural education, and youth programs. Together, we can create lasting change.',
      becomeMember: 'Become a Member',
      donateNow: 'Donate Now',
      testimonials: {
        title: 'Community Voices',
        subtitle: 'Join the movement for civil and human rights and social justice'
      },
      communityVideo: {
        badge: 'Our Movement',
        title: 'See Our Community in Action',
        subtitle: 'Watch how we come together to defend human rights and empower our community.'
      }
    },

    // About Page
    about: {
      heroTitle: 'About Xicano Rights Union',
      heroSubtitle: 'Leading the fight for civil rights, human rights, and justice for Xicano and Indigenous communities in Minnesota.',
      missionTitle: 'Our Mission',
      missionText: 'The Xicano Rights Union (XRU) is dedicated to ensuring the political, educational, social, and economic equality of Xicano and Indigenous peoples through advocacy, litigation, cultural education, and youth empowerment programs.',
      visionTitle: 'Our Vision',
      visionText: 'A society where Xicano and Indigenous peoples enjoy full equality, their cultural heritage is respected and preserved, and future generations are empowered to lead with pride in their identity.',
      historyTitle: 'Our History',
      historyText: 'Founded in 2025 in the Twin Cities, Minnesota, the Xicano Rights Union emerged in response to ongoing civil and human rights violations affecting Xicano and Indigenous communities. Our founders recognized the urgent need for an organization dedicated to investigating injustices, advocating for policy reform, and holding institutions accountable for discrimination and human rights abuses.',
      valuesTitle: 'Our Values',
      values: {
        justice: { title: 'Justice', desc: 'We pursue accountability and systemic change through advocacy and legal action.' },
        culture: { title: 'Cultural Pride', desc: 'We celebrate and preserve our rich Xicano and Indigenous heritage.' },
        community: { title: 'Community', desc: 'We build power through collective action and mutual support.' },
        education: { title: 'Education', desc: 'We empower through knowledge of our history, rights, and identity.' }
      },
      howWeWork: 'How We Work',
      workAreas: {
        investigation: { title: 'Investigation & Documentation', desc: 'We investigate civil and human rights violations, document systemic discrimination, and gather evidence to support advocacy and legal action.' },
        advocacy: { title: 'Advocacy & Legal Action', desc: 'We advocate for policy reforms at local, state, and federal levels, and pursue legal action when necessary to protect civil and human rights.' },
        education: { title: 'Cultural Education', desc: 'We preserve and promote Xicano and Indigenous culture through educational programs, workshops, and community events.' },
        youth: { title: 'Youth Empowerment', desc: 'We invest in the next generation through mentorship, leadership development, and educational opportunities.' },
        mentalHealth: { title: 'Mental Health & Counseling', desc: 'We provide mental health resources and counseling support for community members affected by discrimination, trauma, and human rights abuses. Healing is part of justice.' },
        unity: { title: 'Strengthening Unity', desc: 'We bring our community together through solidarity, mutual aid, and collective action. By strengthening our unity, we empower our future and build lasting power for generations to come.' }
      },
      communityVideo: {
        title: 'Our Movement',
        subtitle: 'See how our community is united in the fight for justice and human rights.',
        watchDocumentary: 'Watch on Tubi'
      },
      testimonials: {
        title: 'Community Voices',
        subtitle: 'Stories from those we\'ve had the privilege to serve and support.'
      }
    },

    // Programs/Our Work Page
    programs: {
      heroTitle: 'Our Work',
      heroSubtitle: 'Fighting for justice, preserving culture, and empowering communities through direct action and advocacy.',
      focusAreas: 'Our Focus Areas',
      priorities: {
        title: 'Our Priorities',
        items: [
          'Investigating and documenting civil and human rights violations',
          'Advocating for policy reform at local, state, and federal levels',
          'Providing cultural education and heritage preservation programs',
          'Supporting youth development and leadership training',
          'Building coalitions with allied organizations',
          'Offering community resources and emergency support'
        ]
      },
      getInvolved: 'Get Involved',
      getInvolvedText: 'If you or someone you know has experienced discrimination or a civil or human rights violation, we want to hear from you.',
      reportViolation: 'Report a Violation',
      joinXRU: 'Join XRU'
    },

    // Contact Page
    contact: {
      heroTitle: 'Contact Us',
      heroSubtitle: 'Report a civil or human rights violation, become a member, or learn how you can support our mission.',
      getInTouch: 'Get in Touch',
      sendMessage: 'Send us a Message',
      formLabels: {
        name: 'Your Name',
        email: 'Email Address',
        subject: 'Subject',
        message: 'Your Message',
        selectSubject: 'Select a subject'
      },
      subjects: {
        general: 'General Inquiry',
        violation: 'Report a Violation',
        membership: 'Membership Question',
        volunteer: 'Volunteer Opportunity',
        media: 'Media Inquiry',
        other: 'Other'
      },
      responseTime: 'We typically respond within 24-48 hours during business days.',
      urgentMatters: 'For urgent civil or human rights matters, email us with "URGENT" in the subject line.',
      faq: {
        title: 'Frequently Asked Questions',
        questions: [
          {
            q: 'How do I report a civil or human rights violation?',
            a: 'You can report a violation through our Report a Violation page, by emailing us directly, or by calling during business hours. All reports are confidential.'
          },
          {
            q: 'How can I become a member of XRU?',
            a: 'Visit our Join XRU page to sign up for membership. We offer different membership levels to accommodate everyone who wants to support our mission.'
          },
          {
            q: 'Does XRU provide legal representation?',
            a: 'XRU advocates for civil and human rights through various means including legal action. While we don\'t provide individual legal representation, we work with partner organizations and may pursue cases that address systemic issues affecting our community.'
          },
          {
            q: 'How can I volunteer with XRU?',
            a: 'We welcome volunteers! Contact us through this form with "Volunteer" as the subject, and we\'ll connect you with opportunities that match your skills and interests.'
          }
        ]
      }
    },

    // Alert Map Page
    alertMap: {
      heroTitle: 'Human Rights Alert Map',
      heroSubtitle: 'Protecting human rights in our community. Report enforcement activity and help keep our neighbors safe.',
      reportActivity: 'Report Activity',
      viewReports: 'View Reports',
      installApp: 'Install App',
      reportsToday: 'Reports Today',
      activeAreas: 'Active Areas',
      lastUpdate: 'Last Update',
      reportForm: {
        title: 'Report Enforcement Activity',
        neighborhood: 'Neighborhood',
        selectNeighborhood: 'Select neighborhood',
        location: 'Specific Location',
        locationPlaceholder: 'e.g., Near Target on Lake Street',
        activityType: 'Activity Type',
        selectActivity: 'Select activity type',
        description: 'Description',
        descriptionPlaceholder: 'Describe what you observed (number of agents, vehicles, actions taken, etc.)',
        addPhoto: 'Add Photo (Optional)',
        photoNote: 'Photos help document activity. Max 5MB, JPG/PNG only.',
        changePhoto: 'Change Photo',
        submit: 'Submit Report',
        submitting: 'Submitting...',
        disclaimer: 'Your report helps protect our community. All information is used solely for community safety alerts.',
        successTitle: 'Report Submitted',
        successMessage: 'Thank you for helping keep our community informed. Your report has been added to the map.'
      },
      activityTypes: {
        ice_presence: 'ICE Presence',
        checkpoint: 'Checkpoint',
        raid: 'Raid/Arrest',
        patrol: 'Patrol Vehicle',
        other: 'Other Activity'
      },
      activityFeed: {
        title: 'Recent Activity',
        noReports: 'No reports today',
        noReportsSubtitle: 'Be the first to report activity in your area',
        reportedAt: 'Reported at'
      },
      emergencyResources: {
        title: 'Emergency Resources',
        subtitle: 'Know your rights. Get help.',
        legalHotlines: 'Legal Hotlines',
        mentalHealth: 'Mental Health & Counseling',
        viewAllResources: 'View All Legal Resources'
      },
      knowYourRights: {
        title: 'Know Your Rights',
        rights: [
          'You have the right to remain silent',
          'You do not have to open your door without a warrant signed by a judge',
          'You have the right to speak with a lawyer',
          'You can refuse to sign documents you don\'t understand'
        ]
      },
      reportViolations: {
        title: 'Report Civil & Human Rights Violations',
        subtitle: 'Document civil and human rights violations and report them to XRU. We investigate and advocate for justice.'
      },
      mapLegend: {
        title: 'Map Legend',
        recentReport: 'Recent Report (< 1 hour)',
        olderReport: 'Older Report'
      }
    },

    // Legal Help Page
    legalHelp: {
      heroTitle: 'Legal Help & Resources',
      heroSubtitle: 'Free and low-cost legal services for immigration, civil rights, human rights, and family law in the Twin Cities area. You have rights — know them and protect them.',
      badge: 'Civil & Human Rights Legal Resources',
      emergencyBanner: {
        title: 'Detained or facing deportation?',
        subtitle: 'Call ILCM immediately.'
      },
      knowYourRights: {
        silent: { title: 'Right to Remain Silent', desc: 'You have the right to remain silent. You don\'t have to answer questions about your immigration status.' },
        attorney: { title: 'Right to an Attorney', desc: 'You have the right to speak with a lawyer before answering any questions or signing any documents.' },
        refuseEntry: { title: 'Right to Refuse Entry', desc: 'You do not have to open your door unless officers have a warrant signed by a judge.' }
      },
      categories: {
        immigration: 'Immigration Legal Services',
        proBono: 'Pro Bono & Legal Aid Clinics',
        family: 'Family Law & Civil Rights',
        humanRights: 'Human Rights Organizations',
        additional: 'Additional Resources'
      },
      reportViolation: {
        title: 'Experienced a Civil or Human Rights Violation?',
        subtitle: 'Document and report violations to help us investigate, advocate for justice, and protect our community from future abuses.',
        reportButton: 'Report a Violation',
        viewMap: 'View Alert Map'
      }
    },

    // Report Violation Page
    reportViolation: {
      heroTitle: 'Report a Violation',
      heroSubtitle: 'Document civil and human rights violations to help us investigate, advocate, and pursue justice.',
      formTitle: 'Submit a Report',
      formLabels: {
        name: 'Your Name',
        email: 'Email Address',
        phone: 'Phone Number (Optional)',
        incidentDate: 'Date of Incident',
        location: 'Location of Incident',
        description: 'Description of Violation'
      },
      placeholders: {
        name: 'Your full name',
        email: 'your@email.com',
        phone: '(555) 123-4567',
        location: 'Address or description of location',
        description: 'Please describe the violation in detail. Include what happened, who was involved, and any witnesses present.'
      },
      submit: 'Submit Report',
      submitting: 'Submitting...',
      successTitle: 'Report Submitted',
      successMessage: 'Thank you for reporting this civil or human rights violation. Our team will review your submission and may contact you for additional information.',
      confidential: 'All reports are confidential and will be reviewed by our team.',
      needHelp: 'Need Immediate Help?',
      needHelpText: 'If you are in immediate danger or need urgent legal assistance, contact these resources:'
    },

    // Join XRU Page
    joinXRU: {
      heroTitle: 'Join the Movement',
      heroSubtitle: 'Stand with us in the fight for civil and human rights, cultural preservation, and justice.',
      membershipLevels: 'Membership Levels',
      levels: {
        community: { name: 'Community Member', price: 'Free', desc: 'Join our community and stay informed about civil and human rights issues.' },
        supporter: { name: 'Supporter', price: '$25/year', desc: 'Support our advocacy work and receive exclusive updates.' },
        advocate: { name: 'Advocate', price: '$50/year', desc: 'Become an active advocate with voting rights and event access.' },
        champion: { name: 'Champion', price: '$100/year', desc: 'Lead the movement with premium benefits and recognition.' }
      },
      benefits: {
        title: 'Member Benefits',
        items: [
          'Regular updates on civil and human rights issues',
          'Invitations to community events and workshops',
          'Access to educational resources',
          'Opportunity to participate in advocacy campaigns',
          'Connection with like-minded community members'
        ]
      },
      formTitle: 'Sign Up',
      formLabels: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number (Optional)',
        membershipType: 'Membership Level',
        interests: 'Areas of Interest'
      },
      interests: {
        advocacy: 'Advocacy & Policy',
        legal: 'Legal Support',
        education: 'Cultural Education',
        youth: 'Youth Programs',
        volunteer: 'Volunteering',
        events: 'Events & Outreach'
      },
      submit: 'Join XRU',
      submitting: 'Joining...',
      successTitle: 'Welcome to XRU!',
      successMessage: 'Thank you for joining the Xicano Rights Union. Together, we will fight for justice and protect our community.',
      whyJoin: {
        title: 'Why Join XRU?',
        reasons: [
          { title: 'Protect Your Community', desc: 'Stand together against civil and human rights violations and support those affected by unjust policies.' },
          { title: 'Preserve Our Culture', desc: 'Help preserve and celebrate Xicano and Indigenous heritage for future generations.' },
          { title: 'Empower Youth', desc: 'Support programs that educate and empower the next generation of leaders.' }
        ]
      }
    },

    // Partners Page
    partners: {
      heroTitle: 'Our Partners',
      heroSubtitle: 'We are grateful to the organizations and individuals who stand with us in the fight for civil and human rights and justice.',
      sponsors: {
        title: 'Our Sponsors',
        subtitle: 'Organizations that support our mission for civil and human rights and justice'
      },
      communityPartners: {
        title: 'Community Partners',
        subtitle: 'Organizations we work alongside to serve our community'
      },
      becomePartner: {
        title: 'Become a Partner',
        subtitle: 'Join us in our mission to defend rights and empower communities. We welcome partnerships with organizations that share our values.',
        contact: 'Contact Us About Partnership'
      }
    },

    // News Page
    news: {
      heroTitle: 'News & Updates',
      heroSubtitle: 'Tracking civil rights and human rights violations in Minneapolis. Stay informed. Stay vigilant. Stay connected.',
      latestNews: 'Latest News',
      categories: {
        all: 'All',
        legalAction: 'Legal Action',
        memorial: 'Memorial',
        community: 'Community',
        investigation: 'Investigation'
      },
      reportTip: {
        title: 'Report Violations',
        text: 'If you witness ICE activity or experience a civil or human rights violation, document everything and report it to us.'
      }
    },

    // Newsletter
    newsletter: {
      title: 'Stay Informed',
      subtitle: 'Subscribe to receive updates on civil and human rights issues, cultural education programs, and community events.',
      placeholder: 'Enter your email',
      subscribe: 'Subscribe',
      subscribing: 'Subscribing...',
      success: 'Thank you for subscribing!',
      privacy: 'We respect your privacy. Unsubscribe anytime.'
    },

    // Footer
    footer: {
      description: 'The Xicano Rights Union is a human rights organization dedicated to ensuring the political, educational, social, and economic equality of Xicano and Indigenous peoples through advocacy, litigation, cultural education, and youth empowerment programs.',
      copyright: '© {year} Xicano Rights Union. All rights reserved.',
      tagline: 'A Human Rights Organization'
    }
  },

  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      about: 'Nosotros',
      ourWork: 'Nuestro Trabajo',
      news: 'Noticias',
      legalHelp: 'Ayuda Legal',
      partners: 'Socios',
      contact: 'Contacto',
      alertMap: 'Mapa de Alertas',
      reportViolation: 'Reportar Violación',
      joinXRU: 'Únete a XRU'
    },
    
    // Common
    common: {
      learnMore: 'Más Información',
      submit: 'Enviar',
      sending: 'Enviando...',
      close: 'Cerrar',
      viewAll: 'Ver Todo',
      readMore: 'Leer Más',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      name: 'Nombre',
      message: 'Mensaje',
      location: 'Ubicación',
      date: 'Fecha',
      time: 'Hora',
      description: 'Descripción',
      subscribe: 'Suscribirse',
      joinMovement: 'Únete al Movimiento',
      reportNow: 'Reportar Ahora',
      callNow: 'Llamar Ahora',
      visitWebsite: 'Visitar Sitio Web',
      quickLinks: 'Enlaces Rápidos',
      contactUs: 'Contáctenos',
      followUs: 'Síguenos',
      allRightsReserved: 'Todos los derechos reservados',
      defendingRights: 'Defendiendo los Derechos Humanos. Exigiendo Justicia. Construyendo Poder.',
      humanRights: 'Derechos Humanos',
      civilRights: 'Derechos Civiles'
    },

    // Home Page
    home: {
      heroTitle: 'Defendiendo los Derechos Humanos de los Pueblos Xicanos e Indígenas',
      heroSubtitle: 'La Unión de Derechos Xicanos lucha por los derechos humanos, derechos civiles y la dignidad de las comunidades Xicanas e Indígenas a través de la defensa, acción legal, educación cultural y empoderamiento juvenil.',
      reportViolation: 'Reportar una Violación',
      joinMovement: 'Únete al Movimiento',
      missionQuote: '"Toda persona merece derechos humanos. Abogamos por políticas que desmantelan las injusticias sistémicas, preservan nuestra herencia cultural y empoderan a la próxima generación de líderes Xicanos e Indígenas."',
      topStories: 'Historias Principales',
      viewAllNews: 'Ver Todas las Noticias',
      whatWeDo: 'Lo Que Hacemos',
      whatWeDoSubtitle: 'XRU se dedica a defender los derechos humanos, luchar por los derechos civiles, preservar el patrimonio cultural y empoderar a nuestra juventud a través de la educación y la organización comunitaria.',
      alertMapBanner: {
        title: 'Mapa de Alertas de Derechos Humanos',
        subtitle: 'Reporta y rastrea actividad de aplicación en las Ciudades Gemelas. Protege los derechos humanos. Mantén segura a nuestra comunidad.',
        viewMap: 'Ver Mapa de Alertas',
        liveUpdates: 'Actualizaciones en Vivo'
      },
      joinFight: 'Únete a la Lucha por la Justicia',
      joinFightSubtitle: 'Tu voz importa. Tu membresía apoya nuestra defensa, educación cultural y programas juveniles. Juntos, podemos crear un cambio duradero.',
      becomeMember: 'Hazte Miembro',
      donateNow: 'Donar Ahora',
      testimonials: {
        title: 'Voces de la Comunidad',
        subtitle: 'Únete al movimiento por los derechos civiles y humanos y la justicia social'
      },
      communityVideo: {
        badge: 'Nuestro Movimiento',
        title: 'Mira Nuestra Comunidad en Acción',
        subtitle: 'Observa cómo nos unimos para defender los derechos humanos y empoderar a nuestra comunidad.'
      }
    },

    // About Page
    about: {
      heroTitle: 'Sobre la Unión de Derechos Xicanos',
      heroSubtitle: 'Liderando la lucha por los derechos civiles, derechos humanos y justicia para las comunidades Xicanas e Indígenas en Minnesota.',
      missionTitle: 'Nuestra Misión',
      missionText: 'La Unión de Derechos Xicanos (XRU) se dedica a asegurar la igualdad política, educativa, social y económica de los pueblos Xicanos e Indígenas a través de la defensa, litigio, educación cultural y programas de empoderamiento juvenil.',
      visionTitle: 'Nuestra Visión',
      visionText: 'Una sociedad donde los pueblos Xicanos e Indígenas disfruten de plena igualdad, donde su herencia cultural sea respetada y preservada, y donde las generaciones futuras estén empoderadas para liderar con orgullo de su identidad.',
      historyTitle: 'Nuestra Historia',
      historyText: 'Fundada en 2025 en las Ciudades Gemelas, Minnesota, la Unión de Derechos Xicanos surgió en respuesta a las continuas violaciones de derechos civiles y humanos que afectan a las comunidades Xicanas e Indígenas. Nuestros fundadores reconocieron la urgente necesidad de una organización dedicada a investigar injusticias, abogar por reformas políticas y responsabilizar a las instituciones por la discriminación y los abusos de derechos humanos.',
      valuesTitle: 'Nuestros Valores',
      values: {
        justice: { title: 'Justicia', desc: 'Buscamos responsabilidad y cambio sistémico a través de la defensa y acción legal.' },
        culture: { title: 'Orgullo Cultural', desc: 'Celebramos y preservamos nuestra rica herencia Xicana e Indígena.' },
        community: { title: 'Comunidad', desc: 'Construimos poder a través de la acción colectiva y el apoyo mutuo.' },
        education: { title: 'Educación', desc: 'Empoderamos a través del conocimiento de nuestra historia, derechos e identidad.' }
      },
      howWeWork: 'Cómo Trabajamos',
      workAreas: {
        investigation: { title: 'Investigación y Documentación', desc: 'Investigamos violaciones de derechos civiles y humanos, documentamos discriminación sistémica y recopilamos evidencia para apoyar la defensa y acción legal.' },
        advocacy: { title: 'Defensa y Acción Legal', desc: 'Abogamos por reformas políticas a nivel local, estatal y federal, y emprendemos acciones legales cuando es necesario para proteger los derechos civiles y humanos.' },
        education: { title: 'Educación Cultural', desc: 'Preservamos y promovemos la cultura Xicana e Indígena a través de programas educativos, talleres y eventos comunitarios.' },
        youth: { title: 'Empoderamiento Juvenil', desc: 'Invertimos en la próxima generación a través de mentoría, desarrollo de liderazgo y oportunidades educativas.' },
        mentalHealth: { title: 'Salud Mental y Consejería', desc: 'Proporcionamos recursos de salud mental y apoyo de consejería para miembros de la comunidad afectados por discriminación, trauma y abusos de derechos humanos. La sanación es parte de la justicia.' },
        unity: { title: 'Fortaleciendo la Unidad', desc: 'Unimos a nuestra comunidad a través de la solidaridad, ayuda mutua y acción colectiva. Al fortalecer nuestra unidad, empoderamos nuestro futuro y construimos poder duradero para las generaciones venideras.' }
      },
      communityVideo: {
        title: 'Nuestro Movimiento',
        subtitle: 'Mira cómo nuestra comunidad está unida en la lucha por la justicia y los derechos humanos.',
        watchDocumentary: 'Ver en Tubi'
      },
      testimonials: {
        title: 'Voces de la Comunidad',
        subtitle: 'Historias de aquellos que hemos tenido el privilegio de servir y apoyar.'
      }
    },

    // Programs/Our Work Page
    programs: {
      heroTitle: 'Nuestro Trabajo',
      heroSubtitle: 'Luchando por la justicia, preservando la cultura y empoderando a las comunidades a través de la acción directa y la defensa.',
      focusAreas: 'Nuestras Áreas de Enfoque',
      priorities: {
        title: 'Nuestras Prioridades',
        items: [
          'Investigar y documentar violaciones de derechos civiles y humanos',
          'Abogar por reformas políticas a nivel local, estatal y federal',
          'Proporcionar programas de educación cultural y preservación del patrimonio',
          'Apoyar el desarrollo juvenil y la formación de liderazgo',
          'Construir coaliciones con organizaciones aliadas',
          'Ofrecer recursos comunitarios y apoyo de emergencia'
        ]
      },
      getInvolved: 'Involúcrate',
      getInvolvedText: 'Si tú o alguien que conoces ha experimentado discriminación o una violación de derechos civiles o humanos, queremos saber de ti.',
      reportViolation: 'Reportar una Violación',
      joinXRU: 'Únete a XRU'
    },

    // Contact Page
    contact: {
      heroTitle: 'Contáctenos',
      heroSubtitle: 'Reporta una violación de derechos civiles o humanos, hazte miembro o aprende cómo puedes apoyar nuestra misión.',
      getInTouch: 'Ponte en Contacto',
      sendMessage: 'Envíanos un Mensaje',
      formLabels: {
        name: 'Tu Nombre',
        email: 'Correo Electrónico',
        subject: 'Asunto',
        message: 'Tu Mensaje',
        selectSubject: 'Selecciona un asunto'
      },
      subjects: {
        general: 'Consulta General',
        violation: 'Reportar una Violación',
        membership: 'Pregunta sobre Membresía',
        volunteer: 'Oportunidad de Voluntariado',
        media: 'Consulta de Medios',
        other: 'Otro'
      },
      responseTime: 'Normalmente respondemos dentro de 24-48 horas durante días hábiles.',
      urgentMatters: 'Para asuntos urgentes de derechos civiles o humanos, envíanos un correo con "URGENTE" en el asunto.',
      faq: {
        title: 'Preguntas Frecuentes',
        questions: [
          {
            q: '¿Cómo reporto una violación de derechos civiles o humanos?',
            a: 'Puedes reportar una violación a través de nuestra página Reportar una Violación, enviándonos un correo directamente, o llamando durante horas de oficina. Todos los reportes son confidenciales.'
          },
          {
            q: '¿Cómo puedo hacerme miembro de XRU?',
            a: 'Visita nuestra página Únete a XRU para registrarte como miembro. Ofrecemos diferentes niveles de membresía para acomodar a todos los que quieran apoyar nuestra misión.'
          },
          {
            q: '¿XRU proporciona representación legal?',
            a: 'XRU aboga por los derechos civiles y humanos a través de varios medios incluyendo acción legal. Aunque no proporcionamos representación legal individual, trabajamos con organizaciones asociadas y podemos perseguir casos que aborden problemas sistémicos que afectan a nuestra comunidad.'
          },
          {
            q: '¿Cómo puedo ser voluntario con XRU?',
            a: '¡Damos la bienvenida a voluntarios! Contáctanos a través de este formulario con "Voluntario" como asunto, y te conectaremos con oportunidades que coincidan con tus habilidades e intereses.'
          }
        ]
      }
    },

    // Alert Map Page
    alertMap: {
      heroTitle: 'Mapa de Alertas de Derechos Humanos',
      heroSubtitle: 'Protegiendo los derechos humanos en nuestra comunidad. Reporta actividad de aplicación y ayuda a mantener seguros a nuestros vecinos.',
      reportActivity: 'Reportar Actividad',
      viewReports: 'Ver Reportes',
      installApp: 'Instalar App',
      reportsToday: 'Reportes Hoy',
      activeAreas: 'Áreas Activas',
      lastUpdate: 'Última Actualización',
      reportForm: {
        title: 'Reportar Actividad de Aplicación',
        neighborhood: 'Vecindario',
        selectNeighborhood: 'Selecciona vecindario',
        location: 'Ubicación Específica',
        locationPlaceholder: 'ej., Cerca de Target en Lake Street',
        activityType: 'Tipo de Actividad',
        selectActivity: 'Selecciona tipo de actividad',
        description: 'Descripción',
        descriptionPlaceholder: 'Describe lo que observaste (número de agentes, vehículos, acciones tomadas, etc.)',
        addPhoto: 'Agregar Foto (Opcional)',
        photoNote: 'Las fotos ayudan a documentar la actividad. Máximo 5MB, solo JPG/PNG.',
        changePhoto: 'Cambiar Foto',
        submit: 'Enviar Reporte',
        submitting: 'Enviando...',
        disclaimer: 'Tu reporte ayuda a proteger a nuestra comunidad. Toda la información se usa únicamente para alertas de seguridad comunitaria.',
        successTitle: 'Reporte Enviado',
        successMessage: 'Gracias por ayudar a mantener informada a nuestra comunidad. Tu reporte ha sido agregado al mapa.'
      },
      activityTypes: {
        ice_presence: 'Presencia de ICE',
        checkpoint: 'Punto de Control',
        raid: 'Redada/Arresto',
        patrol: 'Vehículo de Patrulla',
        other: 'Otra Actividad'
      },
      activityFeed: {
        title: 'Actividad Reciente',
        noReports: 'Sin reportes hoy',
        noReportsSubtitle: 'Sé el primero en reportar actividad en tu área',
        reportedAt: 'Reportado a las'
      },
      emergencyResources: {
        title: 'Recursos de Emergencia',
        subtitle: 'Conoce tus derechos. Obtén ayuda.',
        legalHotlines: 'Líneas Legales de Emergencia',
        mentalHealth: 'Salud Mental y Consejería',
        viewAllResources: 'Ver Todos los Recursos Legales'
      },
      knowYourRights: {
        title: 'Conoce Tus Derechos',
        rights: [
          'Tienes derecho a permanecer en silencio',
          'No tienes que abrir tu puerta sin una orden firmada por un juez',
          'Tienes derecho a hablar con un abogado',
          'Puedes negarte a firmar documentos que no entiendas'
        ]
      },
      reportViolations: {
        title: 'Reportar Violaciones de Derechos Civiles y Humanos',
        subtitle: 'Documenta violaciones de derechos civiles y humanos y repórtalas a XRU. Investigamos y abogamos por la justicia.'
      },
      mapLegend: {
        title: 'Leyenda del Mapa',
        recentReport: 'Reporte Reciente (< 1 hora)',
        olderReport: 'Reporte Anterior'
      }
    },

    // Legal Help Page
    legalHelp: {
      heroTitle: 'Ayuda Legal y Recursos',
      heroSubtitle: 'Servicios legales gratuitos y de bajo costo para inmigración, derechos civiles, derechos humanos y derecho familiar en el área de las Ciudades Gemelas. Tienes derechos — conócelos y protégelos.',
      badge: 'Recursos Legales de Derechos Civiles y Humanos',
      emergencyBanner: {
        title: '¿Detenido o enfrentando deportación?',
        subtitle: 'Llama a ILCM inmediatamente.'
      },
      knowYourRights: {
        silent: { title: 'Derecho a Permanecer en Silencio', desc: 'Tienes derecho a permanecer en silencio. No tienes que responder preguntas sobre tu estatus migratorio.' },
        attorney: { title: 'Derecho a un Abogado', desc: 'Tienes derecho a hablar con un abogado antes de responder cualquier pregunta o firmar cualquier documento.' },
        refuseEntry: { title: 'Derecho a Negar Entrada', desc: 'No tienes que abrir tu puerta a menos que los oficiales tengan una orden firmada por un juez.' }
      },
      categories: {
        immigration: 'Servicios Legales de Inmigración',
        proBono: 'Clínicas Pro Bono y de Asistencia Legal',
        family: 'Derecho Familiar y Derechos Civiles',
        humanRights: 'Organizaciones de Derechos Humanos',
        additional: 'Recursos Adicionales'
      },
      reportViolation: {
        title: '¿Experimentaste una Violación de Derechos Civiles o Humanos?',
        subtitle: 'Documenta y reporta violaciones para ayudarnos a investigar, abogar por la justicia y proteger a nuestra comunidad de futuros abusos.',
        reportButton: 'Reportar una Violación',
        viewMap: 'Ver Mapa de Alertas'
      }
    },

    // Report Violation Page
    reportViolation: {
      heroTitle: 'Reportar una Violación',
      heroSubtitle: 'Documenta violaciones de derechos civiles y humanos para ayudarnos a investigar, abogar y buscar justicia.',
      formTitle: 'Enviar un Reporte',
      formLabels: {
        name: 'Tu Nombre',
        email: 'Correo Electrónico',
        phone: 'Número de Teléfono (Opcional)',
        incidentDate: 'Fecha del Incidente',
        location: 'Ubicación del Incidente',
        description: 'Descripción de la Violación'
      },
      placeholders: {
        name: 'Tu nombre completo',
        email: 'tu@correo.com',
        phone: '(555) 123-4567',
        location: 'Dirección o descripción de la ubicación',
        description: 'Por favor describe la violación en detalle. Incluye qué pasó, quién estuvo involucrado y cualquier testigo presente.'
      },
      submit: 'Enviar Reporte',
      submitting: 'Enviando...',
      successTitle: 'Reporte Enviado',
      successMessage: 'Gracias por reportar esta violación de derechos civiles o humanos. Nuestro equipo revisará tu envío y puede contactarte para información adicional.',
      confidential: 'Todos los reportes son confidenciales y serán revisados por nuestro equipo.',
      needHelp: '¿Necesitas Ayuda Inmediata?',
      needHelpText: 'Si estás en peligro inmediato o necesitas asistencia legal urgente, contacta estos recursos:'
    },

    // Join XRU Page
    joinXRU: {
      heroTitle: 'Únete al Movimiento',
      heroSubtitle: 'Únete a nosotros en la lucha por los derechos civiles y humanos, la preservación cultural y la justicia.',
      membershipLevels: 'Niveles de Membresía',
      levels: {
        community: { name: 'Miembro Comunitario', price: 'Gratis', desc: 'Únete a nuestra comunidad y mantente informado sobre temas de derechos civiles y humanos.' },
        supporter: { name: 'Apoyo', price: '$25/año', desc: 'Apoya nuestro trabajo de defensa y recibe actualizaciones exclusivas.' },
        advocate: { name: 'Defensor', price: '$50/año', desc: 'Conviértete en un defensor activo con derechos de voto y acceso a eventos.' },
        champion: { name: 'Campeón', price: '$100/año', desc: 'Lidera el movimiento con beneficios premium y reconocimiento.' }
      },
      benefits: {
        title: 'Beneficios de Membresía',
        items: [
          'Actualizaciones regulares sobre temas de derechos civiles y humanos',
          'Invitaciones a eventos comunitarios y talleres',
          'Acceso a recursos educativos',
          'Oportunidad de participar en campañas de defensa',
          'Conexión con miembros de la comunidad con ideas afines'
        ]
      },
      formTitle: 'Regístrate',
      formLabels: {
        name: 'Nombre Completo',
        email: 'Correo Electrónico',
        phone: 'Número de Teléfono (Opcional)',
        membershipType: 'Nivel de Membresía',
        interests: 'Áreas de Interés'
      },
      interests: {
        advocacy: 'Defensa y Política',
        legal: 'Apoyo Legal',
        education: 'Educación Cultural',
        youth: 'Programas Juveniles',
        volunteer: 'Voluntariado',
        events: 'Eventos y Alcance'
      },
      submit: 'Unirse a XRU',
      submitting: 'Uniéndose...',
      successTitle: '¡Bienvenido a XRU!',
      successMessage: 'Gracias por unirte a la Unión de Derechos Xicanos. Juntos, lucharemos por la justicia y protegeremos a nuestra comunidad.',
      whyJoin: {
        title: '¿Por Qué Unirse a XRU?',
        reasons: [
          { title: 'Protege Tu Comunidad', desc: 'Únete contra las violaciones de derechos civiles y humanos y apoya a los afectados por políticas injustas.' },
          { title: 'Preserva Nuestra Cultura', desc: 'Ayuda a preservar y celebrar la herencia Xicana e Indígena para las futuras generaciones.' },
          { title: 'Empodera a la Juventud', desc: 'Apoya programas que educan y empoderan a la próxima generación de líderes.' }
        ]
      }
    },

    // Partners Page
    partners: {
      heroTitle: 'Nuestros Socios',
      heroSubtitle: 'Estamos agradecidos con las organizaciones e individuos que nos apoyan en la lucha por los derechos civiles y humanos y la justicia.',
      sponsors: {
        title: 'Nuestros Patrocinadores',
        subtitle: 'Organizaciones que apoyan nuestra misión por los derechos civiles y humanos y la justicia'
      },
      communityPartners: {
        title: 'Socios Comunitarios',
        subtitle: 'Organizaciones con las que trabajamos para servir a nuestra comunidad'
      },
      becomePartner: {
        title: 'Conviértete en Socio',
        subtitle: 'Únete a nosotros en nuestra misión de defender los derechos y empoderar a las comunidades. Damos la bienvenida a asociaciones con organizaciones que comparten nuestros valores.',
        contact: 'Contáctanos Sobre Asociación'
      }
    },

    // News Page
    news: {
      heroTitle: 'Noticias y Actualizaciones',
      heroSubtitle: 'Rastreando violaciones de derechos civiles y humanos en Minneapolis. Mantente informado. Mantente vigilante. Mantente conectado.',
      latestNews: 'Últimas Noticias',
      categories: {
        all: 'Todas',
        legalAction: 'Acción Legal',
        memorial: 'Memorial',
        community: 'Comunidad',
        investigation: 'Investigación'
      },
      reportTip: {
        title: 'Reportar Violaciones',
        text: 'Si eres testigo de actividad de ICE o experimentas una violación de derechos civiles o humanos, documenta todo y repórtalo a nosotros.'
      }
    },

    // Newsletter
    newsletter: {
      title: 'Mantente Informado',
      subtitle: 'Suscríbete para recibir actualizaciones sobre temas de derechos civiles y humanos, programas de educación cultural y eventos comunitarios.',
      placeholder: 'Ingresa tu correo electrónico',
      subscribe: 'Suscribirse',
      subscribing: 'Suscribiendo...',
      success: '¡Gracias por suscribirte!',
      privacy: 'Respetamos tu privacidad. Cancela en cualquier momento.'
    },

    // Footer
    footer: {
      description: 'La Unión de Derechos Xicanos es una organización de derechos humanos dedicada a asegurar la igualdad política, educativa, social y económica de los pueblos Xicanos e Indígenas a través de la defensa, litigio, educación cultural y programas de empoderamiento juvenil.',
      copyright: '© {year} Unión de Derechos Xicanos. Todos los derechos reservados.',
      tagline: 'Una Organización de Derechos Humanos'
    }
  }
};

export default LanguageContext;
